import { type FC, useCallback, useRef, useState } from 'react'

import './_profileImageModal.scss'
import { Cropper } from 'react-cropper'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import Webcam from 'react-webcam'

import { addToast } from 'components/Toast/Toast'
import ImgInCircle from 'components/UI/ImgInCircle/ImgInCircle'
import { useModalContext } from 'context/modalContext'
import { Icons } from 'helpers/icons'
import { presignPhoto, uploadFileToS3 } from 'services/endpoints/attachment'
import { putAvatarImage, putCoverImage } from 'services/endpoints/profile'
import { useSetUserProfile, useUserProfile } from 'store'
import { useProfileCacheStore } from 'store/profileCacheStore'
import { useProfileFeedCacheStore } from 'store/profileFeedCacheStore'
import { type ImageAndCropImage } from 'types/api/profile'
import { type IProfile } from 'types/interfaces/IProfile'
import { safeRandomUUID } from 'utils/safeRandomUUID'

import 'cropperjs/dist/cropper.css'

const CDN_URL = 'https://image-cdn.allfans.com'
const COVER_ASPECT_RATIO = 3

const patchAvatarInQueryData = (
  value: any,
  userId: string,
  avatarUrl: string
): any => {
  if (value == null) {
    return value
  }

  if (Array.isArray(value)) {
    let changed = false

    const next = value.map(item => {
      const patched = patchAvatarInQueryData(
        item,
        userId,
        avatarUrl
      )

      if (patched !== item) {
        changed = true
      }

      return patched
    })

    return changed ? next : value
  }

  if (typeof value !== 'object') {
    return value
  }

  let changed = false
  let nextObject = value

  if (
    value?.user &&
    String(value.user.id) === String(userId)
  ) {
    if (
      value.user?.avatar?.url !== avatarUrl
    ) {
      nextObject = {
        ...value,
        user: {
          ...value.user,
          avatar: {
            ...(value.user.avatar ?? {}),
            url: avatarUrl,
          },
        },
      }

      changed = true
    }
  }

  const source = nextObject
  let recursiveResult = source

  for (const [key, child] of Object.entries(source)) {
    if (key === 'user' && changed) {
      continue
    }

    const patchedChild =
      patchAvatarInQueryData(
        child,
        userId,
        avatarUrl
      )

    if (patchedChild !== child) {
      if (recursiveResult === source) {
        recursiveResult = {
          ...source,
        }
      }

      recursiveResult[key] = patchedChild
      changed = true
    }
  }

  return changed
    ? recursiveResult
    : value
}


const publicUrlFromKey = (key?: string) =>
  key ? `${CDN_URL}/${key.replace(/^\/+/, '')}` : undefined

const ProfileImageModal: FC<{
  isCover?: boolean
  closeNestedModal?: () => void
}> = ({ isCover = false, closeNestedModal }) => {
  const [crop, setCrop] = useState(false)
  const [cameraActive, setCameraActive] = useState(false)
  const [cameraSelfie, setCameraSelfie] = useState(true)
  const [cropActive, setCropActive] = useState(false)
  const [avatar, setAvatar] = useState<{ img: string | File; blob: string }>({
    img: '',
    blob: '',
  })
  const [isUploading, setIsUploading] = useState(false)
  const [imageError, setImageError] = useState<string | null>(null)
  const userData = useUserProfile()
  const getProfileFromCache = useProfileCacheStore(state => state.getProfile)
  const setProfileInCache = useProfileCacheStore(state => state.setProfile)
  const [zoom, setZoom] = useState(0)
  const modalData = useModalContext()
  const uploadRef = useRef<HTMLInputElement>(null)
  const webcamRef = useRef<any>(null)
  const cropperRef = useRef<HTMLImageElement>(null)
const baseZoomRatioRef = useRef<number | null>(null)
  const { t } = useTranslation()

  const queryClient = useQueryClient()

  const updateUserAvatar =
    useProfileFeedCacheStore(
      state => state.updateUserAvatar
    )

  const closeModal = () => {
    closeNestedModal ? closeNestedModal() : modalData.clearModal()
  }

  const changePage = () => {
    setCrop(prev => !prev)
    setCameraActive(false)
  }

  const makeImageKey = () => {
    const base = isCover ? 'covers' : 'avatars'
    return `public/${base}/${userData?.id}/${safeRandomUUID()}.jpg`
  }

const patchAvatarCaches = (
  avatarUrl: string
) => {
  if (!userData?.id || !avatarUrl) {
    return
  }

  const userId = userData.id

  updateUserAvatar(
    userId,
    avatarUrl
  )

  queryClient.setQueriesData(
    ['getFeed', userId],
    (    cached: any) =>
      patchAvatarInQueryData(
        cached,
        userId,
        avatarUrl
      )
  )
}

  
  const patchProfileImageCache = ({ image }: ImageAndCropImage) => {
    if (!userData?.username || !userData?.id || !image) return

    const cached = getProfileFromCache(userData.username, userData.id)
    const baseProfile = cached ?? (userData as unknown as IProfile)

    setProfileInCache(
      userData.username,
      isCover
        ? {
            ...baseProfile,
            cover: {
              ...(baseProfile.cover ?? {}),
              url: image,
            },
          }
        : {
            ...baseProfile,
            avatar: {
              ...(baseProfile.avatar ?? {}),
              url: image,
            },
          },
      userData.id
    )
  }

const updateFile = (
  e: React.FormEvent<HTMLInputElement>
) => {
  const file = e.currentTarget.files?.[0]

  if (!file) return

  setImageError(null)

  const allowedTypes = [
    'image/jpeg',
    'image/png',
    'image/webp'
  ]

  if (!allowedTypes.includes(file.type)) {
    setImageError(
      'Images must be JPEG, PNG, or WebP.'
    )

    e.currentTarget.value = ''
    return
  }

  const objectUrl = URL.createObjectURL(file)
  const image = new Image()

  image.onload = () => {
    const width = image.naturalWidth
    const height = image.naturalHeight

    URL.revokeObjectURL(objectUrl)

    if (width > 12000 || height > 12000) {
      setImageError(
        'Images must be 12,000 × 12,000 pixels or smaller.'
      )

      if (uploadRef.current) {
        uploadRef.current.value = ''
      }

      return
    }

    setImageError(null)

    baseZoomRatioRef.current = null
    setZoom(25)

    setAvatar({
      img: file,
      blob: URL.createObjectURL(file)
    })

    setCropActive(true)
    changePage()
  }

  image.onerror = () => {
    URL.revokeObjectURL(objectUrl)

    if (uploadRef.current) {
      uploadRef.current.value = ''
    }

    setImageError(
      'Unable to read this image.'
    )
  }

  image.src = objectUrl
}
  const getCroppedFile = async (): Promise<File> => {
    const cropper = (cropperRef.current as any)?.cropper

    if (!cropper) throw new Error('Cropper not ready')

    const canvas: HTMLCanvasElement = cropper.getCroppedCanvas({
      width: isCover ? 1500 : 768,
      height: isCover ? 500 : 768,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high'
    })

    if (!canvas) throw new Error('Failed to get cropped canvas')

    return await new Promise((resolve, reject) => {
      canvas.toBlob(
        blob => {
          if (!blob) {
            reject(new Error(t('error:errorFailedGettingCroppedBlob')))
            return
          }

          resolve(
            new File([blob], 'image-cropped.jpg', {
              type: 'image/jpeg',
            })
          )
        },
        'image/jpeg',
        0.9
      )
    })
  }

  const uploadImage = async (file: File) => {
    const key = makeImageKey()
    const { url, key: returnedKey } = await presignPhoto({
      contentType: file.type || 'image/jpeg',
      key,
      type: isCover ? 'cover' : 'avatar',
      size: file.size
    })

    await uploadFileToS3(url, file)

    return returnedKey
  }

const saveImages = async (
  backendImages: ImageAndCropImage,
  cacheImages: ImageAndCropImage
) => {
  if (isCover) {
    await putCoverImage(backendImages)

    if (cacheImages.image) {
      useSetUserProfile({
        cover: {
          id: userData?.cover?.id ?? null,
          url: cacheImages.image,
          created_at: userData?.cover?.created_at ?? null
        }
      })
    }
  } else {
    await putAvatarImage(backendImages)

    if (cacheImages.image) {
      useSetUserProfile({
        avatar: {
          id: userData?.avatar?.id ?? null,
          url: cacheImages.image,
          created_at: userData?.avatar?.created_at ?? null
        }
      })
    }
  }

  patchProfileImageCache(cacheImages)

  if (!isCover && cacheImages.image) {
    patchAvatarCaches(cacheImages.image)
  }
}
const uploadFile = async () => {
  try {
    setIsUploading(true)
    addToast('loading', t('uploadingFilePleaseWait'))

    if (!(avatar.img instanceof File)) {
      throw new Error('No image file selected')
    }

    const finalFile = cropActive
      ? await getCroppedFile()
      : avatar.img

    const finalKey = await uploadImage(finalFile)
    const finalUrl = publicUrlFromKey(finalKey)!

    await saveImages(
      {
        image: finalKey,
        cropped_image: finalKey
      },
      {
        image: finalUrl,
        cropped_image: finalUrl
      }
    )

    toast.dismiss()
    setIsUploading(false)
    closeModal()

  } catch (err) {
    console.error(err)
    toast.dismiss()
    setIsUploading(false)
    addToast(
      'error',
      t('error:errorSomethingWentWrong'),
      undefined,
      true
    )
  }
}

  const capture = useCallback(async () => {
    const imgSrc = webcamRef.current.getScreenshot()
    const file = await fetch(imgSrc)
      .then(async r => await r.blob())
      .then(blobFile => new File([blobFile], 'photo.jpg', { type: 'image/jpeg' }))

    setAvatar({ img: file, blob: imgSrc })
    changePage()
  }, [])

  return (
    <div
      className={`profileImageModal ${
        isUploading ? 'profileImageModal__disabled' : ''
      }`}
    >
      {!crop && (
        <>
          <input
            ref={uploadRef}
            type="file"
            onChange={updateFile}
            id="profile__avatar"
            accept="image/jpeg"
            hidden
          />

          <div
            className="profileImageModal__block"
            onClick={() => uploadRef.current?.click()}
          >
            <div className="profileImageModal__img">
              <img src={Icons.img} alt={t('uploadPic')} />
            </div>
            <p className="profileImageModal__text">{t('uploadPhoto')}</p>
          </div>

          {cameraActive && (
            <div className="profileImageModal__camera">
              <Webcam
                className="profileImageModal__camera__cam"
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                videoConstraints={{
                  facingMode: cameraSelfie
                    ? 'user'
                    : { exact: 'environment' },
                }}
              />

              <div
                className="profileImageModal__camera__capture"
                onClick={capture}
              />

              <div
                className="profileImageModal__camera__rotate"
                onClick={() => setCameraSelfie(prev => !prev)}
              >
                <img src={Icons.rotate} alt={t('rotate')} />
              </div>

              <div
                className="profileImageModal__camera__close"
                onClick={() => setCameraActive(false)}
              >
                <img src={Icons.close} alt={t('closeModal')} />
              </div>
            </div>
          )}

          <div
            className="profileImageModal__block"
            onClick={() => setCameraActive(prev => !prev)}
          >
            <div className="profileImageModal__img">
              <img src={Icons.cameraBlack} alt={t('takePhoto')} />
            </div>
            <p className="profileImageModal__text">{t('takePhoto')}</p>
          </div>
        </>
      )}
{imageError && (
  <div className="profileImageModal__error">
    {imageError}
  </div>
)}
      {crop && (
        <div
className={`profileImageModal__crop ${
  isCover
    ? 'profileImageModal__crop__isCover'
    : 'profileImageModal__crop__isProfile'
}`}
        >
<div className="profileImageModal__crop__block">
  <Cropper
    src={avatar.blob}
    style={{
      width: '100%',
      height: '100%'
    }}
    ref={cropperRef}
    viewMode={1}
    dragMode="move"
    movable={true}
    zoomable={true}
    zoomOnTouch={true}
    zoomOnWheel={false}
    aspectRatio={isCover ? COVER_ASPECT_RATIO : 1}
    autoCrop={true}
    autoCropArea={1}
    cropBoxMovable={false}
    cropBoxResizable={false}
    guides={false}
    center={false}
    highlight={false}
    background={false}
    modal={true}
    responsive={true}
ready={() => {
  const cropper = (cropperRef.current as any)?.cropper

  if (!cropper) return

  const container = cropper.getContainerData()
  const image = cropper.getImageData()

  if (
    !container.width ||
    !container.height ||
    !image.naturalWidth ||
    !image.naturalHeight
  ) {
    return
  }

  const baseRatio = isCover
    ? container.width / image.naturalWidth
    : Math.max(
        container.width / image.naturalWidth,
        container.height / image.naturalHeight
      )

  baseZoomRatioRef.current = baseRatio

  cropper.zoomTo(baseRatio)

  window.requestAnimationFrame(() => {
    const canvas = cropper.getCanvasData()

    cropper.setCanvasData({
      left: (container.width - canvas.width) / 2,
      top: (container.height - canvas.height) / 2,
      width: canvas.width,
      height: canvas.height
    })

    if (isCover) {
      const cropWidth = container.width
      const cropHeight = cropWidth / COVER_ASPECT_RATIO

      cropper.setCropBoxData({
        left: 0,
        top: (container.height - cropHeight) / 2,
        width: cropWidth,
        height: cropHeight
      })
    } else {
      const cropSize = Math.min(
        container.width,
        container.height
      )

      cropper.setCropBoxData({
        left: (container.width - cropSize) / 2,
        top: (container.height - cropSize) / 2,
        width: cropSize,
        height: cropSize
      })
    }

    setZoom(25)
  })
}}
  />

  <div className="profileImageModal__zoom">
    <span className="profileImageModal__zoom__icon">−</span>

    <input
      type="range"
      min="25"
      max="100"
      step="1"
      value={zoom}
      onChange={e => {
        const nextZoom = Number(e.currentTarget.value)
        const cropper =
          (cropperRef.current as any)?.cropper

        const baseRatio =
          baseZoomRatioRef.current

        if (!cropper || baseRatio == null) {
          return
        }

        /*
         * 25 is the minimum cover zoom.
         * 100 is four times the cover zoom.
         */
        const multiplier = nextZoom / 25

        cropper.zoomTo(baseRatio * multiplier)
        setZoom(nextZoom)
      }}
      aria-label={t('zoom')}
    />

    <span className="profileImageModal__zoom__icon">+</span>
  </div>
</div>

          <div className="profileImageModal__crop__actions">
            <ImgInCircle
              type="modal"
              customClass="profileImageModal__crop__action"
              clickFn={changePage}
            >
              <img src={Icons.back} alt={t('return')} />
            </ImgInCircle>

            <ImgInCircle
              type="modal"
              customClass="profileImageModal__crop__action"
              clickFn={() => setCropActive(prev => !prev)}
            >
              <img src={Icons.cropimg} alt={t('cropPic')} />
            </ImgInCircle>

            <ImgInCircle
              type="modal"
              customClass={`profileImageModal__crop__action ${
                !isCover ? 'profileImageModal__crop__action__rotate' : ''
              }`}
            >
              <img src={Icons.reload} alt={t('reload')} />
            </ImgInCircle>

            <ImgInCircle
              type="modal"
              customClass="profileImageModal__crop__action"
              clickFn={uploadFile}
            >
              <img src={Icons.checkmark} alt={t('accept')} />
            </ImgInCircle>
          </div>
          
        </div>
      )}
    </div>
  )
}

export default ProfileImageModal